import { twMerge } from "tailwind-merge";
import { Check } from "lucide-react";
import { Type } from "@themes/pollinator/ui/Type";
import type { Action } from "@core/features/actions";
import { TimezoneOption } from "@core/features/timezones";
import { ProgramPhaseActionButton } from "./ProgramPhaseActionButton";
import { usePhaseAction } from "@platform/features/programActions";

type Props = {
  action: Action;
  disabled: boolean;
  userTimezoneOption: TimezoneOption;
  /** When true, always use the compact (mobile) layout: top border only, no rounded card */
  compact?: boolean;
};

export function ProgramPhaseActionItem({
  action,
  disabled,
  userTimezoneOption,
  compact = false,
}: Props) {
  const { name, description } = action;

  const { label, onClick, statusLabel, isCompleted, isAvailable } =
    usePhaseAction(action, userTimezoneOption.name);

  return (
    <>
      <div
        className={twMerge(
          "flex flex-col p-4 h-full justify-between border-t border-brand-700 max-w-3xl",
          !compact && "md:flex-row md:rounded-md md:border md:items-center",
          ((isCompleted && !isAvailable) || disabled) && "opacity-50 flex-row"
        )}
      >
        <div className="flex flex-col gap-2 md:pr-4 overflow-hidden">
          <Type variant="h5" className="flex items-center gap-2">
            {isCompleted ? (
              <Check
                className="w-4 h-4 text-brand-600 flex-shrink-0"
                strokeWidth={3}
                aria-label="Completed action"
              />
            ) : (
              <span
                className="inline-block w-2 h-2 rounded-full bg-red-600 flex-shrink-0"
              />
            )}
            {name}{" "}
            {statusLabel && (
              <span className="text-brand-700">({statusLabel})</span>
            )}
          </Type>
          <Type variant="caption" className={twMerge("text-primary max-w-lg", compact && "pl-4")}>
            {description}
          </Type>
        </div>
        <ProgramPhaseActionButton
          label={label}
          isAvailable={isAvailable}
          isCompleted={isCompleted}
          isDisabled={disabled}
          isButtonDisabled={false}
          onClick={onClick}
          compact={compact}
        />
      </div>
    </>
  );
}

export default ProgramPhaseActionItem;

